import React, { type ReactNode } from "react";
import { Provider } from "react-redux";
import { type Store } from "redux";
import { type Jss } from "jss";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { CssBaseline } from "@material-ui/core";
import { StylesProvider } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

import { ThemeContextProvider } from "src/contexts/ThemeContext";
import { LoadingBackdropProvider } from "src/contexts/LoadingBackdropContext";
import { STRIPE_PUBLIC_KEY } from "./config";
import { SalesTransactionsDataLoaderProvider } from "./utils/userData/SalesTransactionsDataLoaderContext";

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

interface ProvidersProps {
  store: Store;
  jss?: Jss;
  /** Skip Firestore sales/tx loader (e.g. print iframe — avoids a throwaway DataLoaderManager). */
  disableSalesTransactionsDataLoader?: boolean;
  children: ReactNode;
}

function Providers({
  store,
  jss,
  disableSalesTransactionsDataLoader = false,
  children,
}: ProvidersProps) {
  const inner = (
    <ThemeContextProvider>
      <Elements
        stripe={stripePromise}
        options={{
          fonts: [
            {
              cssSrc:
                "https://fonts.googleapis.com/css2?family=Roboto&display=swap",
            },
          ],
        }}
      >
        <CssBaseline />
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <LoadingBackdropProvider>{children}</LoadingBackdropProvider>
        </MuiPickersUtilsProvider>
      </Elements>
    </ThemeContextProvider>
  );

  const content = (
    <Provider store={store}>
      {disableSalesTransactionsDataLoader ? (
        inner
      ) : (
        <SalesTransactionsDataLoaderProvider>
          {inner}
        </SalesTransactionsDataLoaderProvider>
      )}
    </Provider>
  );

  if (jss) {
    return <StylesProvider jss={jss}>{content}</StylesProvider>;
  }

  return content;
}

export default Providers;
